/**
 * Markdown for the AVA Assistant: stream-safe source, token parsing, and the link allow-list.
 *
 * Model text is never rendered with v-html. `parseMarkdown` returns `marked` lexer tokens and the
 * MarkdownView component turns them into VNodes, so raw HTML in a reply can only ever show as text.
 * While a reply streams, `streamSafeSource` holds back constructs that are still incomplete (an
 * open code fence, a half table row, an unclosed `**`), the same rule Astryx's Markdown follows,
 * so the reader never sees raw `|` or `**` flash before the closing chunk arrives.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { marked } from 'marked';

const FENCE = /^\s*```/;
const BOLD = '**';

/** Index of the last line that opens or closes a code fence, and how many there are. */
function fenceInfo(lines) {
  let count = 0;
  let lastLine = -1;
  lines.forEach((line, i) => {
    if (FENCE.test(line)) {
      count += 1;
      lastLine = i;
    }
  });
  return { count, lastLine };
}

/** Positions of `**` that sit outside fenced code blocks. */
function boldPositionsOutsideFences(src) {
  const positions = [];
  let inFence = false;
  let offset = 0;
  src.split('\n').forEach((line) => {
    if (FENCE.test(line)) inFence = !inFence;
    else if (!inFence) {
      for (let i = line.indexOf(BOLD); i >= 0; i = line.indexOf(BOLD, i + BOLD.length)) positions.push(offset + i);
    }
    offset += line.length + 1;
  });
  return positions;
}

const TABLE_LINE = /^\s*\|/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

/**
 * A trailing block of `|` lines is held back until its second line is the `|---|` separator;
 * before that, marked renders the header row as a paragraph of raw pipes.
 */
function withholdTableWithoutSeparator(src) {
  const lines = src.split('\n');
  let end = lines.length;
  if (lines[end - 1] === '') end -= 1;
  let start = end;
  while (start > 0 && TABLE_LINE.test(lines[start - 1])) start -= 1;
  if (start === end) return src;
  const block = lines.slice(start, end);
  if (block.length >= 2 && TABLE_SEPARATOR.test(block[1])) return src;
  return lines.slice(0, start).join('\n') + (start > 0 ? '\n' : '');
}

/**
 * The longest prefix of `src` that is safe to render while more text is still arriving.
 * @param {string} src
 * @returns {string}
 */
export function streamSafeSource(src) {
  if (!src) return '';
  let safe = src;

  const lines = safe.split('\n');
  const fences = fenceInfo(lines);
  if (fences.count % 2 === 1) safe = lines.slice(0, fences.lastLine).join('\n') + (fences.lastLine > 0 ? '\n' : '');

  const lastNewline = safe.lastIndexOf('\n');
  if (safe.slice(lastNewline + 1).trimStart().startsWith('|')) safe = safe.slice(0, lastNewline + 1);
  safe = withholdTableWithoutSeparator(safe);

  const bold = boldPositionsOutsideFences(safe);
  if (bold.length % 2 === 1) safe = safe.slice(0, bold[bold.length - 1]);

  return safe;
}

/**
 * @param {string} src
 * @returns {Array<object>} marked lexer tokens (GitHub-flavoured)
 */
export function parseMarkdown(src) {
  return marked.lexer(src || '', { gfm: true });
}

/** A number, optionally signed, with thousands separators or a %; dates and codes ("2026-10-20", "3-GC-204") are not. */
const NUMERIC = /^[+-]?\d[\d,]*(\.\d+)?%?$/;
/** Table cells right-align only when they hold a number. */
export const isNumericCell = (text) => NUMERIC.test(String(text || '').trim());

/** Callout kinds the reply format allows (contract: "Reply format"); others stay normal quotes. */
export const CALLOUT_KINDS = Object.freeze(['important', 'warning', 'tip', 'note']);
const CALLOUT_MARKER = /^\s*\[!([a-z]+)\][ \t]*\n?/i;

/**
 * A GitHub-style alert (`> [!WARNING]` …) as `{ kind, tokens }`, the body re-parsed without the
 * marker. Null for any other token or kind.
 * @param {object} token a marked block token
 */
export function calloutOf(token) {
  if (!token || token.type !== 'blockquote' || typeof token.text !== 'string') return null;
  const match = token.text.match(CALLOUT_MARKER);
  const kind = match && match[1].toLowerCase();
  if (!kind || !CALLOUT_KINDS.includes(kind)) return null;
  return { kind, tokens: parseMarkdown(token.text.slice(match[0].length)) };
}

const SAFE_URL = /^(https?:\/\/|mailto:|\/(?!\/))/i;
/** Whitespace, control characters and backslashes: browsers strip or rewrite them while resolving
 *  a URL, so `/\evil.com` or `/<tab>/evil.com` would turn an "app path" into another host. */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\s\\\u0000-\u001f\u007f]/;
const PROBE_ORIGIN = 'https://ava.invalid';

/**
 * The same-origin path for an app link ("/facilities/12?tab=a"), or null when the value is not
 * one, so router navigation can never leave AVA. Used by markdown links, link chips and artifacts.
 * @param {unknown} href
 * @returns {string|null}
 */
export function toAppPath(href) {
  if (typeof href !== 'string') return null;
  const value = href.trim();
  if (!value.startsWith('/') || value.startsWith('//') || UNSAFE_CHARS.test(value)) return null;
  try {
    const url = new URL(value, PROBE_ORIGIN);
    return url.origin === PROBE_ORIGIN ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch (err) {
    return null;
  }
}

/**
 * A full URL that points at the app itself (same scheme, host and port as `appOrigin`, e.g.
 * window.location.origin) as an app path, so it opens in-app instead of as an outside link.
 * Models sometimes paste the absolute links tools return. Anything else returns null.
 * @param {unknown} href
 * @param {string} appOrigin
 * @returns {string|null}
 */
export function sameOriginAppPath(href, appOrigin) {
  if (typeof href !== 'string' || typeof appOrigin !== 'string' || !appOrigin) return null;
  const value = href.trim();
  if (!/^https?:\/\//i.test(value) || UNSAFE_CHARS.test(value)) return null;
  try {
    const url = new URL(value);
    if (url.origin !== new URL(appOrigin).origin) return null;
    return toAppPath(`${url.pathname}${url.search}${url.hash}`);
  } catch (err) {
    return null;
  }
}

/**
 * A router location the agent may ask the UI to open: a safe app path (see toAppPath) or a named
 * route `{ name, params?, query? }`. Anything else (raw URLs, `{ path }` objects) returns null.
 * @param {unknown} to
 * @returns {string|{ name: string, params?: object, query?: object }|null}
 */
export function toAppLocation(to) {
  if (typeof to === 'string') return toAppPath(to);
  if (to && typeof to === 'object' && typeof to.name === 'string' && to.name) {
    const location = { name: to.name };
    if (to.params && typeof to.params === 'object') location.params = { ...to.params };
    if (to.query && typeof to.query === 'object') location.query = { ...to.query };
    return location;
  }
  return null;
}

/**
 * Links in model output become anchors only for http(s), mailto and app-relative paths.
 * Everything else (javascript:, data:, vbscript:, protocol-relative //host, look-alike paths
 * containing whitespace, control characters or backslashes) renders as text.
 * @param {unknown} href
 * @returns {boolean}
 */
export function isSafeUrl(href) {
  if (typeof href !== 'string') return false;
  const value = href.trim();
  if (!value || UNSAFE_CHARS.test(value) || !SAFE_URL.test(value)) return false;
  return value.startsWith('/') ? toAppPath(value) !== null : true;
}
